/**
 * useTagOperations Hook
 * 
 * Хук для операций редактирования и удаления тегов
 * Включает мутации для обновления тегов в базе данных
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { tagKeys } from '@/hooks/useTags';
import { useAuth } from '@/contexts/UnifiedAuthContext';
import type { Tag } from '@/integrations/supabase/types';

interface UpdateTagData {
  name: string;
  color: string;
}

export const useTagOperations = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  type MutationCallbacks<T> = {
    onSuccess?: (data: T) => void;
    onError?: (error: Error) => void;
  };

  // Мутация для обновления тега
  const updateTagMutation = useMutation({
    mutationFn: async ({ tagId, data }: { tagId: string; data: UpdateTagData }) => {
      const { data: updatedTag, error } = await supabase
        .from('tags')
        .update({
          name: data.name,
          color: data.color
        })
        .eq('id', tagId)
        .select()
        .single();

      if (error) throw error;
      return updatedTag;
    },
    
    // Оптимистичные обновления
    onMutate: async ({ tagId, data }) => {
      if (!user?.id) return;
      
      // Отменяем все исходящие запросы для userTags
      await queryClient.cancelQueries({ queryKey: tagKeys.userTags(user.id) });
      
      // Сохраняем предыдущие данные для отката
      const previousTags = queryClient.getQueryData<Tag[]>(tagKeys.userTags(user.id));
      
      // Оптимистично обновляем кэш
      queryClient.setQueryData<Tag[]>(
        tagKeys.userTags(user.id),
        (old) => {
          if (!old) return [];
          return old.map(tag => 
            tag.id === tagId 
              ? { ...tag, name: data.name, color: data.color }
              : tag
          );
        }
      );
      
      // Возвращаем контекст для возможного отката
      return { previousTags };
    },
    onSuccess: (updatedTag) => {
      // Инвалидируем кэши тегов с правильными ключами
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: tagKeys.userTags(user.id) });
      }
      queryClient.invalidateQueries({ queryKey: ['record-tags'] });
      queryClient.invalidateQueries({ queryKey: ['records'] });

      toast({
        title: 'Тег обновлен',
        description: `Тег "${updatedTag.name}" успешно обновлен`,
      });
    },
    onError: (error, variables, context) => {
      // Откатываем оптимистичные изменения если есть ошибка
      if (context && user?.id) {
        queryClient.setQueryData(tagKeys.userTags(user.id), context.previousTags);
      }
      
      console.error('Error updating tag:', error);
      toast({
        title: 'Ошибка',
        description: 'Не удалось обновить тег. Попробуйте еще раз.',
        variant: 'destructive',
      });
    },
  });

  // Мутация для удаления тега
  const deleteTagMutation = useMutation({
    mutationFn: async (tagId: string) => {
      // Сначала удаляем все связи с записями
      const { error: recordTagsError } = await supabase
        .from('record_tags')
        .delete()
        .eq('tag_id', tagId);

      if (recordTagsError) throw recordTagsError;

      // Затем удаляем сам тег (мягкое удаление)
      const { error: tagError } = await supabase
        .from('tags')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', tagId);

      if (tagError) throw tagError;

      return tagId;
    },
    onSuccess: (deletedTagId) => {
      // Инвалидируем кэши тегов с правильными ключами
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: tagKeys.userTags(user.id) });
      }
      queryClient.invalidateQueries({ queryKey: ['record-tags'] });
      queryClient.invalidateQueries({ queryKey: ['records'] });

      toast({
        title: 'Тег удален',
        description: 'Тег и все его связи с записями успешно удалены',
      });
    },
    onError: (error) => {
      console.error('Error deleting tag:', error);
      toast({
        title: 'Ошибка',
        description: 'Не удалось удалить тег. Попробуйте еще раз.',
        variant: 'destructive',
      });
    },
  });

  const updateTagWithCallbacks = (
    { tagId, data }: { tagId: string; data: UpdateTagData },
    callbacks?: MutationCallbacks<Tag>
  ) => {
    updateTagMutation.mutate(
      { tagId, data },
      {
        onSuccess: (updatedTag) => {
          callbacks?.onSuccess?.(updatedTag);
        },
        onError: (error) => {
          callbacks?.onError?.(error);
        }
      }
    );
  };

  const deleteTagWithCallbacks = (
    tagId: string,
    callbacks?: MutationCallbacks<string>
  ) => {
    deleteTagMutation.mutate(
      tagId,
      {
        onSuccess: (deletedTagId) => {
          callbacks?.onSuccess?.(deletedTagId);
        },
        onError: (error) => {
          callbacks?.onError?.(error);
        }
      }
    );
  };

  return {
    updateTag: updateTagWithCallbacks,
    deleteTag: deleteTagWithCallbacks,
    isUpdating: updateTagMutation.isPending,
    isDeleting: deleteTagMutation.isPending,
  };
};